import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { publicUrl } from '../crm/publicUrl';
import { trackVisit, trackEvent } from '../crm/visitor';
import ContactModal from '../crm/ContactModal';

const API = '/crm-api';
const SITE = 'https://cartello.in';

// Cartello's service pillars + catalogue, sourced from cartello.in (main-index /
// our-services). No invented metrics — the firm's public site states qualitative
// positioning (international audit/tax/advisory network for SMEs), not numbers.
const SERVICES = [
  { name: 'Business Advisory & Consulting', tag: 'Advisory',
    desc: 'Start-up and investor advisory across every stage of your business — strategy, structure and growth.',
    features: ['Investor & start-up advisory', 'Business plan & project reports', 'Growth consulting'],
    icon: '<path d="M3 7h18v12H3z"/><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2"/>' },
  { name: 'India Entry Strategy & FDI', tag: 'Market Entry',
    desc: 'Plan and implement investment routes into India — market analysis, location studies and FDI facilitation.',
    features: ['Entry strategy', 'Geographical location studies', 'Foreign Direct Investment'],
    icon: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a15 15 0 010 18a15 15 0 010-18z"/>' },
  { name: 'Company Formation & Compliance', tag: 'Corporate',
    desc: 'Incorporation of private, public and LLP entities, plus director ID, digital signatures and statutory filings.',
    features: ['Incorporation (Pvt / Public / LLP)', 'DIN, DSC & statutory filing', 'Shareholder agreements'],
    icon: '<path d="M4 21V5a2 2 0 012-2h8a2 2 0 012 2v16"/><path d="M4 21h16M9 7h2M9 11h2M9 15h2"/>' },
  { name: 'Audit & Assurance', tag: 'Audit',
    desc: 'Internal audits, statutory audits (GAAP / IFRS) and financial statement analysis you can rely on.',
    features: ['Internal audit', 'Statutory audits (GAAP / IFRS)', 'Financial statement analysis'],
    icon: '<path d="M12 3l7 3v6c0 5-3.5 7.5-7 9-3.5-1.5-7-4-7-9V6l7-3z"/><path d="M9 12l2 2 4-4"/>' },
  { name: 'Capital & Corporate Finance', tag: 'Finance',
    desc: 'Capital structuring, IPO / GDR planning, loan syndication and M&A / restructuring support.',
    features: ['Capital structuring & PE/VC', 'IPO / GDR planning', 'Loan syndication · M&A'],
    icon: '<path d="M3 3v18h18"/><path d="M7 15l4-4 3 3 5-6"/>' },
  { name: 'Governance, Risk & BI', tag: 'Risk & BI',
    desc: 'Corporate governance frameworks, SOX / enterprise risk management and Hyperion / Oracle BI implementation.',
    features: ['Corporate governance', 'SOX & enterprise risk', 'Hyperion / Oracle BI'],
    icon: '<path d="M4 20h16"/><path d="M7 20V10M12 20V4M17 20v-7"/>' },
];

// Service pillars — icon + label, no fabricated figures.
const PILLARS = [
  { label: 'Accounting Advisory', sub: 'SMEs to enterprise',
    icon: '<path d="M4 4h16v16H4z"/><path d="M8 8h8M8 12h8M8 16h5"/>' },
  { label: 'Audit & Assurance', sub: 'internal & statutory',
    icon: '<path d="M12 3l7 3v6c0 5-3.5 7.5-7 9-3.5-1.5-7-4-7-9V6l7-3z"/><path d="M9 12l2 2 4-4"/>' },
  { label: 'Tax Compliance', sub: 'direct & indirect',
    icon: '<path d="M9 3h6l1 4H8z"/><path d="M6 7h12l-1 13H7z"/><path d="M10 11v6M14 11v6"/>' },
  { label: 'Risk & Valuation', sub: 'governance & reporting',
    icon: '<path d="M12 3v18"/><path d="M5 8l7-5 7 5"/><path d="M5 8v5a7 4 0 0014 0V8"/>' },
];

// [label, href, brand-colour, icon path] — company social accounts.
const SOCIALS = [
  ['LinkedIn', 'https://cartello.in', '#0a66c2', '<path d="M4.98 3.5A2.5 2.5 0 100 3.51a2.5 2.5 0 004.98 0zM.5 8h4V24h-4zM8 8h3.8v2.2h.05C12.4 8.9 14 8 16 8c4 0 5 2.6 5 6V24h-4v-7.5c0-1.8 0-4.1-2.5-4.1s-2.9 2-2.9 4V24H8z"/>'],
  ['Facebook', 'https://cartello.in', '#1877f2', '<path d="M22 12a10 10 0 10-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0022 12z"/>'],
];

export default function Microsite() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [status, setStatus] = useState('loading');
  const [enquiry, setEnquiry] = useState(null); // null | { interest }

  useEffect(() => {
    fetch(`${API}/public/employee/${slug}`).then((r) => r.json()).then((d) => {
      if (d.success && d.employee) { setEmp(d.employee); setStatus('ok'); }
      else setStatus('notfound');
    }).catch(() => setStatus('notfound'));
    trackVisit(slug, 'microsite'); // CRM: log visitor + open a session
    trackEvent('page_view', 'Opened microsite', { employeeSlug: slug, source: 'microsite' });
  }, [slug]);

  if (status === 'loading') return <div className="min-h-screen flex items-center justify-center text-[#16294d] font-semibold">Loading…</div>;
  if (status === 'notfound') return <div className="min-h-screen flex items-center justify-center text-gray-700">Profile not found</div>;

  const designation = (emp.designation && emp.designation.trim()) || 'Team Member';
  const initials = emp.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const cardUrl = publicUrl(`/profile/${emp.profileSlug || slug}`); // QR hands over the full card

  return (
    <div className="min-h-screen bg-slate-100 text-[#12203a]">
      {/* ---- TOP BAR ---- */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/cartello-logo.png" alt="Cartello" className="w-10 h-10 object-contain shrink-0" />
            <div className="min-w-0">
              <div className="font-bold text-[#16294d] leading-tight truncate">Cartello Software Solutions</div>
              <div className="text-[11px] text-slate-500">Business Advisory · Audit · Tax</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <a href={SITE} target="_blank" rel="noreferrer" className="hidden sm:inline text-sm font-semibold text-slate-600 hover:text-[#2563eb]">Home</a>
            <button onClick={() => navigate(`/profile/${slug}`)} className="text-sm font-semibold text-[#2563eb] hover:underline">Digital Card</button>
            {emp.isAdmin ? (
              <button onClick={() => navigate('/login')}
                className="text-sm font-bold text-white bg-[#2563eb] hover:bg-[#1d4ed8] px-4 py-2 rounded-lg shadow-sm">Admin Login</button>
            ) : (
              <button onClick={() => navigate('/employee/login')}
                className="text-sm font-bold text-white bg-[#2563eb] hover:bg-[#1d4ed8] px-4 py-2 rounded-lg shadow-sm">Emp Login</button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ================= LEFT (services + about) ================= */}
        <div className="lg:col-span-2 space-y-6 order-2 lg:order-1">
          {/* service pillars — above the fold */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {PILLARS.map((st) => (
              <div key={st.label} className="bg-white rounded-2xl border border-slate-100 shadow-sm px-3 py-4 text-center">
                <span className="w-11 h-11 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ background: 'linear-gradient(145deg,#e8eefc,#d5e2fb)' }}>
                  <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="#2563eb" strokeWidth="1.8"
                       strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: st.icon }} />
                </span>
                <div className="text-[13.5px] font-bold text-[#16294d] leading-snug mt-1">{st.label}</div>
                <div className="text-[10.5px] text-slate-400 leading-snug mt-0.5">{st.sub}</div>
              </div>
            ))}
          </div>

          {/* about us */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h2 className="text-lg font-bold text-[#16294d] mb-2">About Cartello Software Solutions</h2>
            <p className="text-slate-600 leading-relaxed text-[15px]">
              Cartello is an international network providing audit, tax and advisory services. We help
              organisations — from small and medium businesses to established enterprises — with accounting,
              financial reporting, statutory compliance and business growth, delivered through a global
              advisor network. Accounting software, advisory services and tax compliance, packaged together.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {['Audit & Assurance', 'Tax Compliance', 'Business Advisory', 'Global Network'].map((c) =>
                <span key={c} className="text-xs font-semibold px-3 py-1 rounded-full bg-[#e8eefc] text-[#16294d] border border-[#bcd0f5]">{c}</span>)}
            </div>
          </div>

          {/* services */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-extrabold text-[#16294d]">Our Services</h2>
              <a href={`${SITE}/our-services.php`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#2563eb] hover:underline">Explore All →</a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SERVICES.map((p) => (
                <div key={p.name} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
                  <div className="flex items-center gap-3 p-4 border-b border-slate-100">
                    <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg,#16294d,#2563eb)' }}>
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: p.icon }} />
                    </span>
                    <h3 className="font-bold text-[15px] text-[#12203a] leading-snug">{p.name}</h3>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <p className="text-slate-500 text-[13px]">{p.desc}</p>
                    <div className="mt-2 space-y-1">
                      {p.features.map((f) => <div key={f} className="text-[12.5px] text-slate-600 flex items-center gap-1.5"><span className="text-[#2563eb]">✓</span>{f}</div>)}
                    </div>
                    <div className="mt-3 text-center text-[12px] font-semibold text-[#16294d] bg-[#e8eefc] rounded-lg py-1.5">{p.tag}</div>
                    <div className="mt-3 flex gap-2">
                      <a href={`${SITE}/our-services.php`} target="_blank" rel="noreferrer" onClick={() => trackEvent('product_view', `Viewed ${p.name}`, { employeeSlug: slug, source: 'microsite' })} className="flex-1 text-center text-sm font-semibold text-[#2563eb] border border-[#bcd0f5] hover:bg-[#e8eefc] rounded-lg py-2">Learn More</a>
                      <button onClick={() => { trackEvent('product_view', `Requested info · ${p.name}`, { employeeSlug: slug, source: 'microsite' }); setEnquiry({ interest: p.name }); }} className="flex-1 text-center text-sm font-semibold text-white bg-[#2563eb] hover:bg-[#1d4ed8] rounded-lg py-2">Enquire</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ================= RIGHT (profile sidebar) ================= */}
        <div className="space-y-6 order-1 lg:order-2">
          <div className="rounded-2xl p-6 text-center lg:sticky lg:top-20 text-[#e8eefb]"
               style={{ background: 'linear-gradient(160deg,#16294d,#0a1424)', border: '1px solid rgba(59,130,246,.25)', boxShadow: '0 24px 60px rgba(0,0,0,.4)' }}>
            <div className="w-28 h-28 rounded-full overflow-hidden mx-auto bg-[#0f2b5c] flex items-center justify-center" style={{ boxShadow: '0 0 0 3px #3b82f6, 0 0 0 7px rgba(59,130,246,.15)' }}>
              <Photo slug={emp.profileSlug} initials={initials} big />
            </div>
            <h1 className="text-xl font-extrabold mt-3 text-white">{emp.name}</h1>
            <div className="text-[#cfe0ff] font-semibold uppercase text-[13px] tracking-wide">{designation}</div>
            <p className="text-[#9db4dc] text-[13px] mt-2">Helping businesses with the right audit, tax and advisory partner for growth.</p>

            <div className="mt-4 space-y-2.5">
              <button type="button"
                onClick={() => { trackEvent('contact', 'Clicked Contact Me on microsite', { employeeSlug: slug, source: 'microsite' }); setEnquiry({ interest: '' }); }}
                className="w-full flex items-center justify-center gap-2 font-bold py-3 rounded-xl shadow hover:brightness-110 text-white"
                style={{ background: 'linear-gradient(145deg,#3b82f6,#2563eb)', boxShadow: '0 5px 14px rgba(37,99,235,.4)' }}>✉️ Contact Me</button>
              {/* QR hands over the full card (phone, email, save-contact). */}
              <div className="flex items-center justify-center gap-3 border border-[#3b82f6]/40 rounded-xl p-2.5">
                <div className="bg-white rounded-lg p-1.5 shrink-0">
                  <QRCodeSVG value={cardUrl} size={72} level="M" />
                </div>
                <div className="text-left">
                  <div className="text-[13px] font-bold text-[#e8eefb] leading-tight">Scan for my card</div>
                  <div className="text-[11.5px] text-[#9db4dc] leading-snug mt-0.5">Phone, email and contact — straight to your phone.</div>
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-3 mt-5">
              {SOCIALS.map(([label, href, color, path]) => (
                <a key={label} href={label === 'LinkedIn' && emp.linkedin ? emp.linkedin : href}
                   target="_blank" rel="noreferrer" title={label} aria-label={label}
                   className="w-9 h-9 rounded-lg flex items-center justify-center hover:brightness-110 transition" style={{ background: color }}>
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="#fff" dangerouslySetInnerHTML={{ __html: path }} />
                </a>
              ))}
            </div>
          </div>

          {/* about me */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h2 className="text-lg font-bold text-[#16294d] mb-2">About Me</h2>
            <p className="text-slate-600 text-[14px] leading-relaxed">
              {emp.name.split(' ')[0]} is part of the Cartello team, helping businesses with accounting,
              audit, tax and advisory services — from India market entry to compliance and corporate finance.
            </p>
            <div className="mt-4 flex items-center gap-3 bg-[#eef4ff] border border-[#bcd0f5] rounded-xl p-3">
              <span className="text-2xl">🏢</span>
              <div><div className="font-bold text-[#16294d] text-sm">Cartello Software Solutions</div><div className="text-[12px] text-[#41506b]">Chennai · Audit · Tax · Advisory</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- FOOTER ---- */}
      <div className="bg-[#0a1424] text-slate-300 mt-4">
        <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
          <div>
            <div className="text-[#7ca8f5] font-bold mb-2">Company Info</div>
            Cartello Software Solutions<br />New No.19, 1st Floor, Playground View Street,<br />Nandanam Extension, Nandanam, Chennai – 600035
          </div>
          <div>
            <div className="text-[#7ca8f5] font-bold mb-2">Contact</div>
            <a href="mailto:varghese@cartello.in" className="hover:text-white">varghese@cartello.in</a><br />
            +91 98410 18117
          </div>
          <div>
            <div className="text-[#7ca8f5] font-bold mb-2">Quick Links</div>
            <a href={SITE} target="_blank" rel="noreferrer" className="hover:text-white">Home</a> ·{' '}
            <button onClick={() => navigate(`/profile/${slug}`)} className="hover:text-white">Digital Card</button> ·{' '}
            <button onClick={() => navigate(emp.isAdmin ? '/login' : '/employee/login')} className="hover:text-white">{emp.isAdmin ? 'Admin Login' : 'Emp Login'}</button>
          </div>
        </div>
        <div className="text-center text-[11px] text-slate-500 pb-6">© 2026 Cartello Software Solutions · Powered by TECGSOFT</div>
      </div>

      {enquiry && <ContactModal slug={slug} source="microsite" handoff={false} presetInterest={enquiry.interest} onClose={() => setEnquiry(null)} />}
    </div>
  );
}

function Photo({ slug, initials, big }) {
  const exts = ['jpg', 'jpeg', 'png', 'webp'];
  const [i, setI] = useState(0);
  if (!slug || i >= exts.length) return <span className={`text-white font-bold ${big ? 'text-3xl' : 'text-2xl'}`}>{initials}</span>;
  return <img src={`/photos/${slug}.${exts[i]}`} alt={initials} onError={() => setI(i + 1)} className="w-full h-full object-cover" />;
}
